import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, ScrollArea, Text, Group, Stack, Badge, Skeleton, useMantineTheme, createStyles, Title, Button, UnstyledButton, ActionIcon } from '@mantine/core';
import { IconFileText, IconArrowsMaximize, IconArrowLeft, IconShirt } from '@tabler/icons-react';
import { SeasonDocumentModel } from 'core/models/SeasonDocumentModel';
import { useFullscreen } from '@mantine/hooks';
import DocumentToolbar from './DocumentToolbar';
import { pluralize } from 'utils/helper';
import { logger } from 'utils/logger';
import StylePopover from '../popovers/StylePopover';
import SeasonStylePopover from '../popovers/SeaonStylePopover';

const useStyles = createStyles((theme) => ({
    container: {
        display: 'flex',
        height: 'calc(100vh - 60px)',
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[0],
        border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
        // borderRadius: theme.radius.md,
        overflow: 'hidden',
        width: '100%', // Ensure container doesn't expand
        touchAction: 'none', // Prevent all touch actions including zoom
        WebkitTouchCallout: 'none', // Prevent callout on iOS
        WebkitUserSelect: 'none', // Prevent selection
        KhtmlUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        userSelect: 'none',
        // Force prevent zoom with vendor prefixes
        zoom: '1.0',
        WebkitTransform: 'scale(1.0)',
        MozTransform: 'scale(1.0)',
        msTransform: 'scale(1.0)',
        transform: 'scale(1.0)',
        // Additional iOS Safari prevention
        WebkitTextSizeAdjust: '100%',
        MozTextSizeAdjust: '100%',
        msTextSizeAdjust: '100%',
        textSizeAdjust: '100%',
    },

    // Left panel - Thumbnails
    thumbnailsPanel: {
        width: 200,
        borderRight: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[1],
        display: 'flex',
        flexDirection: 'column',
    },

    thumbnailsHeader: {
        padding: theme.spacing.sm,
        borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[2],
    },

    thumbnailsList: {
        flex: 1,
        padding: theme.spacing.xs,
    },

    thumbnailItem: {
        cursor: 'pointer',
        padding: theme.spacing.xs,
        marginBottom: theme.spacing.xs,
        borderRadius: theme.radius.sm,
        border: `2px solid transparent`,
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2],
        },
    },

    thumbnailItemActive: {
        // borderColor: theme.colors.blue[6],
        // backgroundColor: theme.colorScheme === 'dark' ? theme.colors.blue[9] : theme.colors.blue[0],
        //  borderColor: theme.colorScheme === 'dark' ? theme.colors.brand[6] : theme.colors.blue[0],
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2],
        img: {
            borderColor: theme.colorScheme === 'dark' ? theme.colors.brand[3] : theme.colors.brand[2],
        },
        // backgroundColor: theme.colorScheme === 'dark' ? theme.colors.brand[0] : theme.colors.blue[0],
    },

    thumbnailImage: {
        width: '100%',
        height: 120,
        objectFit: 'contain',
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        borderRadius: theme.radius.xs,
        border: `3px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[3]}`,
        padding: 2,
        // 'img': {
        //     border: `2px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
        // },
    },

    thumbnailInfo: {
        marginTop: theme.spacing.xs,
    },

    // Center panel - Preview
    previewPanel: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.white,
        minWidth: 0, // Allow flex item to shrink below content size
        overflow: 'hidden', // Contain scrollable content
        touchAction: 'none', // Prevent all touch actions including pinch zoom
        WebkitTouchCallout: 'none', // Prevent callout on iOS
        WebkitUserSelect: 'none', // Prevent selection
        userSelect: 'none',
    },

    previewHeader: {
        padding: theme.spacing.sm,
        borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[0],
    },

    previewContent: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing.md,
        position: 'relative',
        touchAction: 'none', // Prevent default touch behaviors including pinch zoom
        userSelect: 'none', // Prevent text selection during panning
        WebkitTouchCallout: 'none', // Prevent callout on iOS
        WebkitUserSelect: 'none', // Prevent selection on WebKit browsers
        KhtmlUserSelect: 'none', // Prevent selection on Konqueror
        MozUserSelect: 'none', // Prevent selection on Firefox
        msUserSelect: 'none', // Prevent selection on IE/Edge
        WebkitTransform: 'scale(1.0)', // Force prevent zoom
        transform: 'scale(1.0)', // Force prevent zoom
    },

    previewContentStacked: {
        flex: 1,
        position: 'relative',
        padding: 0,
        width: '100%',
        height: 'calc(100vh - 60px)',
        overflow: 'hidden',
        touchAction: 'none', // Prevent default touch behaviors
        userSelect: 'none', // Prevent text selection during panning
        WebkitTouchCallout: 'none', // Prevent callout on iOS
        WebkitUserSelect: 'none', // Prevent selection on WebKit browsers
        KhtmlUserSelect: 'none', // Prevent selection on Konqueror
        MozUserSelect: 'none', // Prevent selection on Firefox
        msUserSelect: 'none', // Prevent selection on IE/Edge
    },

    stackedPagesContainer: {
        padding: theme.spacing.md,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 'fit-content', // Allow container to grow wider than viewport
        position: 'relative', // For intersection observer lines
    },

    intersectionObserverLine: {
        position: 'absolute',
        left: 0,
        right: 0,
        height: '2px',
        backgroundColor: theme.colors.red[5],
        zIndex: 1000,
        pointerEvents: 'none',
        opacity: 0.7,
    },

    intersectionObserverLineTop: {
        top: '10%', // -10% rootMargin from top
    },

    intersectionObserverLineBottom: {
        bottom: '30%', // -30% rootMargin from bottom
    },

    stackedPageItem: {
        position: 'relative',
        marginBottom: theme.spacing.lg,
        '&:last-child': {
            marginBottom: 0,
        },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },

    pageMarker: {
        position: 'absolute',
        top: -theme.spacing.sm, // Reduced from lg to sm for better detection
        left: 0,
        right: 0,
        height: 2, // Increased height slightly for better detection
        opacity: 0, // Hidden marker for intersection observer
        pointerEvents: 'none', // Ensure it doesn't interfere with interactions
    },

    previewImage: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        borderRadius: theme.radius.sm,
        boxShadow: theme.shadows.md,
        touchAction: 'none', // Prevent default touch behaviors
        userSelect: 'none', // Prevent text selection during panning
        // cursor: 'grab', // Show grab cursor to indicate panning
        '&:active': {
            cursor: 'grabbing', // Show grabbing cursor when actively panning
        },
    },

    stackedPreviewImage: {
        width: '100%',
        height: 'auto',
        objectFit: 'contain',
        borderRadius: theme.radius.sm,
        boxShadow: theme.shadows.md,
        border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
        touchAction: 'none', // Prevent default touch behaviors
        userSelect: 'none', // Prevent text selection during panning
        // cursor: 'grab', // Show grab cursor to indicate panning
        '&:active': {
            cursor: 'grabbing', // Show grabbing cursor when actively panning
        },
    },

    previewControls: {
        position: 'absolute',
        bottom: theme.spacing.md,
        right: theme.spacing.md,
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
        borderRadius: theme.radius.md,
        padding: theme.spacing.xs,
        boxShadow: theme.shadows.md,
        border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
    },

    stackedControls: {
        position: 'sticky',
        top: theme.spacing.md,
        right: theme.spacing.md,
        float: 'right',
        marginBottom: theme.spacing.md,
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
        borderRadius: theme.radius.md,
        padding: theme.spacing.xs,
        boxShadow: theme.shadows.md,
        border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
        zIndex: 100,
    },

    // Right panel - Metadata
    metadataPanel: {
        width: 280,
        borderLeft: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[1],
        display: 'flex',
        flexDirection: 'column',
    },

    metadataHeader: {
        padding: theme.spacing.sm,
        borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[2],
    },

    metadataContent: {
        flex: 1,
        padding: theme.spacing.sm,
    },

    metadataSection: {
        marginBottom: theme.spacing.md,
    },

    metadataLabel: {
        fontWeight: 600,
        color: theme.colorScheme === 'dark' ? theme.colors.gray[3] : theme.colors.gray[7],
        marginBottom: theme.spacing.xs,
    },

    metadataValue: {
        color: theme.colorScheme === 'dark' ? theme.colors.gray[0] : theme.colors.gray[8],
    },

    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: theme.colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[6],
    },
}));

const DocumentViewer = ({
    document = null,
    initialPageIndex = 0,
    onPageChange,
    showMetadata = true,
    showThumbnails = true,
    maxHeight = '100%',
    initialLayout = 'single', // 'single' or 'stacked'
    onClose,
    aggData,
}) => {
    const { classes } = useStyles();
    const theme = useMantineTheme();
    const [currentPageIndex, setCurrentPageIndex] = useState(initialPageIndex);
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [layout, setLayout] = useState(initialLayout);
    const [gestureEnabled, setGestureEnabled] = useState(false); // Toggle for pinch/pan functionality
    const [showDebugLines, setShowDebugLines] = useState(false); // Toggle for intersection observer debug lines
    const [showThumbnailsState, setShowThumbnailsState] = useState(showThumbnails);
    const [showMetadataState, setShowMetadataState] = useState(showMetadata);
    const [thumbnailLoadingStates, setThumbnailLoadingStates] = useState(new Map());
    const [mainImageLoadingStates, setMainImageLoadingStates] = useState(new Map());
    const [isManualNavigation, setIsManualNavigation] = useState(false);
    const manualNavigationTimeoutRef = useRef(null);
    const previewRef = useRef(null);
    const stackedContainerRef = useRef(null);
    const pageRefs = useRef({});
    const { toggle } = useFullscreen();
    // Ensure document is a SeasonDocumentModel instance
    const seasonDocument = document instanceof SeasonDocumentModel ? document : null;
    const pages = seasonDocument ? seasonDocument.getAllPagesData() : [];
    const currentPage = pages[currentPageIndex];

    useEffect(() => {
        if (onPageChange && currentPage && seasonDocument) {
            onPageChange(currentPage, currentPageIndex, seasonDocument);
        }
    }, [currentPage, currentPageIndex, onPageChange, seasonDocument]);

    // Calculate optimal zoom for stacked layout
    const calculateOptimalZoom = useCallback(() => {
        if (stackedContainerRef.current) {
            const scrollArea = stackedContainerRef.current;
            const scrollElement = scrollArea.querySelector('[data-radix-scroll-area-viewport]');

            if (scrollElement) {
                // Get available width minus padding (accounting for container padding and some buffer)
                const availableWidth = scrollElement.clientWidth - theme.spacing.md * 6; // Extra padding for comfort
                const maxPageWidth = 800; // Base page width
                const optimalZoom = Math.min(availableWidth / maxPageWidth, 3); // Cap at 3x zoom

                return Math.max(optimalZoom, 0.25); // Ensure minimum zoom of 0.25
            }
        }
        return 1; // Default zoom
    }, [theme.spacing.md]);

    // Set optimal zoom when first switching to stacked layout
    useEffect(() => {
        if (layout === 'stacked' && pages.length > 0) {
            // Use a timeout to ensure the DOM is fully rendered
            const timer = setTimeout(() => {
                const optimalZoom = calculateOptimalZoom();
                setZoom(optimalZoom);
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [layout, pages.length, calculateOptimalZoom]);

    // Handle thumbnail loading states
    const handleThumbnailLoad = useCallback((pageIndex, success = true) => {
        setThumbnailLoadingStates((prev) => {
            const newMap = new Map(prev);
            newMap.set(pageIndex, success ? 'loaded' : 'error');
            return newMap;
        });
    }, []);

    // Handle main document image loading states
    const handleMainImageLoad = useCallback((pageIndex, success = true) => {
        console.log(`Main image ${pageIndex} ${success ? 'loaded' : 'failed'}`);
        setMainImageLoadingStates((prev) => {
            const newMap = new Map(prev);
            newMap.set(pageIndex, success ? 'loaded' : 'error');
            return newMap;
        });
    }, []);

    // Ensure current page image is loaded
    const ensureCurrentPageImageLoaded = useCallback(
        (pageIndex) => {
            if (pages[pageIndex]?.preview && mainImageLoadingStates.get(pageIndex) !== 'loaded') {
                const img = new Image();
                img.onload = () => handleMainImageLoad(pageIndex, true);
                img.onerror = () => handleMainImageLoad(pageIndex, false);
                img.src = pages[pageIndex].preview;
            }
        },
        [pages, mainImageLoadingStates, handleMainImageLoad],
    );

    // Initialize loading states for new pages
    useEffect(() => {
        if (pages.length > 0) {
            console.log(`Initializing loading states for ${pages.length} pages`);
            setThumbnailLoadingStates((prev) => {
                // Only reset if we have new pages or different page count
                const currentPageCount = prev.size;
                if (currentPageCount !== pages.length) {
                    const newLoadingStates = new Map();
                    pages.forEach((_, index) => {
                        newLoadingStates.set(index, 'loading');
                    });
                    console.log('Reset thumbnail loading states:', newLoadingStates);
                    return newLoadingStates;
                }
                return prev;
            });

            setMainImageLoadingStates((prev) => {
                // Only reset if we have new pages or different page count
                const currentPageCount = prev.size;
                if (currentPageCount !== pages.length) {
                    const newLoadingStates = new Map();
                    pages.forEach((_, index) => {
                        newLoadingStates.set(index, 'loading');
                    });
                    console.log('Reset main image loading states:', newLoadingStates);
                    return newLoadingStates;
                }
                return prev;
            });
        }
    }, [pages.length]);

    // Preload thumbnails
    useEffect(() => {
        if (pages.length > 0) {
            pages.forEach((page, index) => {
                if (page.thumbnail || page.preview) {
                    // Check if already loaded to avoid unnecessary preloading
                    if (thumbnailLoadingStates.get(index) !== 'loaded') {
                        const img = new Image();
                        img.onload = () => handleThumbnailLoad(index, true);
                        img.onerror = () => handleThumbnailLoad(index, false);
                        img.src = page.thumbnail || page.preview;
                    }
                }
            });
        }
    }, [pages, handleThumbnailLoad, thumbnailLoadingStates]);

    // Preload main document images
    useEffect(() => {
        if (pages.length > 0) {
            pages.forEach((page, index) => {
                if (page.preview) {
                    // Check if already loaded to avoid unnecessary preloading
                    if (mainImageLoadingStates.get(index) !== 'loaded') {
                        const img = new Image();
                        img.onload = () => handleMainImageLoad(index, true);
                        img.onerror = () => handleMainImageLoad(index, false);
                        img.src = page.preview;
                    }
                }
            });
        }
    }, [pages, handleMainImageLoad, mainImageLoadingStates]);

    // Ensure current page image is loaded when page changes
    useEffect(() => {
        if (currentPageIndex >= 0 && pages.length > 0) {
            ensureCurrentPageImageLoaded(currentPageIndex);
        }
    }, [currentPageIndex, ensureCurrentPageImageLoaded]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (manualNavigationTimeoutRef.current) {
                clearTimeout(manualNavigationTimeoutRef.current);
            }
        };
    }, []);

    // Intersection Observer for stacked layout
    // Replace your existing IntersectionObserver useEffect with this improved version:

    useEffect(() => {
        if (layout !== 'stacked' || !stackedContainerRef.current) return;

        // Debounce function to prevent rapid updates
        let updateTimeout = null;
        const debouncedUpdate = (pageIndex) => {
            if (updateTimeout) clearTimeout(updateTimeout);
            updateTimeout = setTimeout(() => {
                if (!isManualNavigation && pageIndex !== currentPageIndex) {
                    setCurrentPageIndex(pageIndex);
                    logger.log(`debouncedUpdate: ${pageIndex}`);
                }
            }, 150); // Increased debounce to 150ms for more stability
        };

        const observer = new IntersectionObserver(
            (entries) => {
                // Skip all automatic detection during manual navigation
                if (isManualNavigation) {
                    return;
                }

                // Get the scroll container for top check
                const scrollElement = stackedContainerRef.current.querySelector('[data-radix-scroll-area-viewport]');
                if (!scrollElement) return;

                const isAtTop = scrollElement.scrollTop === 0;
                logger.log(`isAtTop: ${isAtTop}`);
                // Handle top position explicitly - keep special treatment for page 0
                if (isAtTop) {
                    if (currentPageIndex !== 0) {
                        debouncedUpdate(0);
                    }
                    return;
                }

                // For non-top positions, find the page with the highest intersection ratio
                // that's also significantly visible
                let bestEntry = null;
                let bestRatio = 0;
                let currentPageEntry = null;
                let page1Entry = null;

                entries.forEach((entry) => {
                    const pageIndex = parseInt(entry.target.dataset.pageIndex, 10);

                    // Track the current page entry
                    if (pageIndex === currentPageIndex) {
                        currentPageEntry = entry;
                    }

                    // Track page 1 entry for special handling
                    if (pageIndex === 1) {
                        page1Entry = entry;
                    }

                    // Only consider entries that are actually intersecting
                    if (entry.isIntersecting && entry.intersectionRatio > bestRatio) {
                        bestRatio = entry.intersectionRatio;
                        bestEntry = entry;
                    }
                });

                // // Special handling for transition from page 0 to page 1
                // if (currentPageIndex === 0 && page1Entry && page1Entry.isIntersecting) {
                //     const page1Ratio = page1Entry.intersectionRatio;
                //     // Switch to page 1 when it becomes significantly visible (lower threshold for page 0→1 transition)
                //     if (page1Ratio > 0.3) {
                //         if (showDebugLines) {
                //             console.log(`Special page 0→1 transition: page1Ratio=${page1Ratio.toFixed(2)}`);
                //         }
                //         debouncedUpdate(1);
                //         return;
                //     }
                // }

                // Only update if we have a clearly dominant page
                if (bestEntry && bestRatio > 0.5) {
                    // Further reduced threshold to 0.5 to catch page 0 and others
                    const pageIndex = parseInt(bestEntry.target.dataset.pageIndex, 10);

                    // Additional check: only switch if the new page is significantly more visible
                    // than the current page, or if current page is not visible
                    const currentPageRatio = currentPageEntry ? currentPageEntry.intersectionRatio : 0;

                    // More lenient switching logic - allow switching if:
                    // 1. Current page is less than 50% visible, OR
                    // 2. New page is at least 15% more visible than current
                    const shouldSwitch = currentPageRatio < 0.5 || bestRatio > currentPageRatio + 0.15;

                    // Debug logging
                    if (showDebugLines) {
                        console.log(`Page ${pageIndex}: ratio=${bestRatio.toFixed(2)}, current=${currentPageIndex}: ratio=${currentPageRatio.toFixed(2)}, shouldSwitch=${shouldSwitch}`);
                    }

                    if (!isNaN(pageIndex) && pageIndex !== currentPageIndex && shouldSwitch) {
                        debouncedUpdate(pageIndex);
                    }
                }
            },
            {
                root: stackedContainerRef.current,
                rootMargin: '-10% 0px -30% 0px', // Balanced margins - less conservative for better detection
                threshold: [0, 0.1, 0.3, 0.5, 0.7, 0.9, 1.0], // More threshold points for better detection
            },
        );

        // Observe all page markers
        Object.values(pageRefs.current).forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        // Scroll handler for page 0 detection
        const scrollElement = stackedContainerRef.current.querySelector('[data-radix-scroll-area-viewport]');
        const handleScroll = () => {
            // Skip during manual navigation
            if (isManualNavigation) {
                return;
            }

            if (scrollElement) {
                const scrollTop = scrollElement.scrollTop;
                logger.log(`scrollTop: ${scrollTop}`, currentPageIndex);
                // Only handle the very top position for page 0
                //  if (scrollTop <= 20 && currentPageIndex !== 0) {
                if (scrollTop === 0 && currentPageIndex !== 0) {
                    debouncedUpdate(0);
                }
                // Let intersection observer handle everything else
            }
        };

        if (scrollElement) {
            // Use throttling for scroll events
            let scrollTimeout = null;
            const throttledScroll = () => {
                if (scrollTimeout) return;
                scrollTimeout = setTimeout(() => {
                    handleScroll();
                    scrollTimeout = null;
                }, 100); // 100ms throttle for stability
            };

            scrollElement.addEventListener('scroll', throttledScroll, { passive: true });

            return () => {
                observer.disconnect();
                scrollElement.removeEventListener('scroll', throttledScroll);
                if (updateTimeout) clearTimeout(updateTimeout);
                if (scrollTimeout) clearTimeout(scrollTimeout);
            };
        }

        return () => {
            observer.disconnect();
            if (updateTimeout) clearTimeout(updateTimeout);
        };
    }, [layout, pages.length, currentPageIndex, isManualNavigation]);

    const handleStackedZoom = (newZoom) => {
        if (!stackedContainerRef.current) {
            setZoom(newZoom);
            return;
        }

        const scrollArea = stackedContainerRef.current;
        const scrollElement = scrollArea.querySelector('[data-radix-scroll-area-viewport]');

        if (!scrollElement) {
            setZoom(newZoom);
            return;
        }

        // Get current scroll position and container dimensions
        const currentScrollLeft = scrollElement.scrollLeft;
        const currentScrollTop = scrollElement.scrollTop;
        const containerWidth = scrollElement.clientWidth;

        // Calculate the center point of the current view
        const centerX = currentScrollLeft + containerWidth / 2;

        // Calculate the relative position (0 to 1) within the scrollable content
        const scrollableWidth = scrollElement.scrollWidth;
        const relativeX = centerX / scrollableWidth;

        // Apply the new zoom
        setZoom(newZoom);

        // Use RAF to ensure the DOM has updated before adjusting scroll
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const newScrollableWidth = scrollElement.scrollWidth;
                const newCenterX = relativeX * newScrollableWidth;
                const newScrollLeft = newCenterX - containerWidth / 2;

                // Scroll to maintain the center position
                scrollElement.scrollTo({
                    left: Math.max(0, newScrollLeft),
                    top: currentScrollTop, // Maintain vertical position
                    behavior: 'auto', // Instant, not smooth
                });
            });
        });
    };

    const handleZoomIn = () => {
        if (layout === 'stacked') {
            handleStackedZoom(Math.min(zoom + 0.25, 3));
        } else {
            setZoom((prev) => Math.min(prev + 0.25, 3));
        }
    };

    const handleZoomOut = () => {
        if (layout === 'stacked') {
            handleStackedZoom(Math.max(zoom - 0.25, 0.25));
        } else {
            setZoom((prev) => Math.max(prev - 0.25, 0.25));
        }
    };
    const handleRotate = () => setRotation((prev) => (prev + 90) % 360);
    const handleReset = () => {
        if (layout === 'stacked') {
            handleStackedZoom(1);
        } else {
            setZoom(1);
            setRotation(0);
        }
        setPan({ x: 0, y: 0 });
    };

    // Toolbar-specific handlers
    const handlePageChange = (pageIndex) => {
        // Clear any existing timeout
        if (manualNavigationTimeoutRef.current) {
            clearTimeout(manualNavigationTimeoutRef.current);
        }

        // Set manual navigation flag to prevent automatic detection
        setIsManualNavigation(true);

        // Set the page index immediately
        setCurrentPageIndex(pageIndex);

        // setZoom(1);
        setRotation(0);
        setPan({ x: 0, y: 0 });

        // Scroll to page in stacked layout
        if (layout === 'stacked' && pageRefs.current[pageIndex]) {
            if (pageIndex === 0) {
                const scrollElement = stackedContainerRef.current.querySelector('[data-radix-scroll-area-viewport]');
                if (scrollElement) {
                    scrollElement.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    });
                }
            } else {
                pageRefs.current[pageIndex].scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
        }

        // Re-enable automatic detection after a delay
        manualNavigationTimeoutRef.current = setTimeout(() => {
            setIsManualNavigation(false);
        }, 1000);
    };

    const handleZoomChange = (newZoom) => {
        if (newZoom === 'extents') {
            const optimalZoom = calculateOptimalZoom();
            setZoom(optimalZoom);
        } else {
            setZoom(newZoom);
        }
        // if (layout === 'stacked') {
        //     handleStackedZoom(newZoom);
        // } else {
        //     setZoom(newZoom);
        // }
    };

    const handleRotationChange = (newRotation) => {
        setRotation(newRotation);
    };

    const handleLayoutChange = (newLayout) => {
        setLayout(newLayout);

        if (newLayout === 'single') {
            setZoom(1);
            setRotation(0);
            setPan({ x: 0, y: 0 });
        } else {
            // For stacked layout, set optimal zoom after a short delay to ensure DOM is ready
            setTimeout(() => {
                const optimalZoom = calculateOptimalZoom();
                setZoom(optimalZoom);

                // Check if we're at the top and set current page to 0 if so
                if (stackedContainerRef.current) {
                    const scrollElement = stackedContainerRef.current.querySelector('[data-radix-scroll-area-viewport]');
                    if (scrollElement && scrollElement.scrollTop === 0) {
                        console.log('Switched to stacked layout at top, setting page to 0');
                        setCurrentPageIndex(0);
                    }
                }
            }, 100);
        }
    };

    const handleDownload = () => {
        console.log('Download document:', seasonDocument?.documentName);
        // TODO: Implement download logic
    };

    const handlePrint = () => {
        console.log('Print document:', seasonDocument?.documentName);
        // TODO: Implement print logic
    };

    // Pointer gesture handling for pinch-to-zoom and panning (like OpenSeadragon)
    const [touchStart, setTouchStart] = useState(null);
    const [initialPinchDistance, setInitialPinchDistance] = useState(null);
    const [initialZoom, setInitialZoom] = useState(1);
    const activePointers = useRef(new Map());

    const getDistance = (touch1, touch2) => {
        return Math.sqrt(Math.pow(touch2.clientX - touch1.clientX, 2) + Math.pow(touch2.clientY - touch1.clientY, 2));
    };

    const getCenter = (touch1, touch2) => {
        return {
            x: (touch1.clientX + touch2.clientX) / 2,
            y: (touch1.clientY + touch2.clientY) / 2,
        };
    };

    // Pointer helpers
    const getTwoPointerData = () => {
        const vals = Array.from(activePointers.current.values());
        if (vals.length < 2) return null;
        return { p1: vals[0], p2: vals[1] };
    };

    const handlePointerDown = useCallback(
        (e) => {
            // If gestures are disabled, don't handle any pointer events
            if (!gestureEnabled) return;

            // Ensure our element keeps receiving subsequent events
            try {
                e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId);
            } catch {}

            activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
            const vals = Array.from(activePointers.current.values());

            // Only prevent default for multi-touch to stop browser zoom
            if (vals.length >= 2 && (e.pointerType === 'touch' || e.pointerType === 'pen')) {
                e.preventDefault();
            }

            if (vals.length === 1) {
                setTouchStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
            } else if (vals.length === 2) {
                const { p1, p2 } = getTwoPointerData();
                const distance = getDistance({ clientX: p1.x, clientY: p1.y }, { clientX: p2.x, clientY: p2.y });
                setInitialPinchDistance(distance);
                setInitialZoom(zoom);
            }
        },
        [pan.x, pan.y, zoom, gestureEnabled],
    );

    const handlePointerMove = useCallback(
        (e) => {
            // If gestures are disabled, don't handle any pointer events
            if (!gestureEnabled) return;

            if (!activePointers.current.has(e.pointerId)) return;

            activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
            const vals = Array.from(activePointers.current.values());

            // Only prevent default for multi-touch during zoom gestures
            if (vals.length >= 2 && (e.pointerType === 'touch' || e.pointerType === 'pen')) {
                e.preventDefault();
            }

            if (vals.length === 2 && initialPinchDistance) {
                const { p1, p2 } = getTwoPointerData();
                const distance = getDistance({ clientX: p1.x, clientY: p1.y }, { clientX: p2.x, clientY: p2.y });
                const scale = distance / initialPinchDistance;
                const newZoom = Math.max(0.25, Math.min(3, initialZoom * scale));
                setZoom(newZoom);
            } else if (vals.length === 1 && touchStart) {
                const newPan = { x: e.clientX - touchStart.x, y: e.clientY - touchStart.y };
                const maxPanX = Math.max(0, (zoom - 1) * 400);
                const maxPanY = Math.max(0, (zoom - 1) * 300);
                newPan.x = Math.max(-maxPanX, Math.min(maxPanX, newPan.x));
                newPan.y = Math.max(-maxPanY, Math.min(maxPanY, newPan.y));
                setPan(newPan);
            }
        },
        [touchStart, initialPinchDistance, initialZoom, zoom, gestureEnabled],
    );

    const handlePointerUpOrCancel = useCallback(
        (e) => {
            // If gestures are disabled, don't handle any pointer events
            if (!gestureEnabled) return;

            if (activePointers.current.has(e.pointerId)) {
                activePointers.current.delete(e.pointerId);
            }
            const vals = Array.from(activePointers.current.values());
            if (vals.length < 2) {
                setInitialPinchDistance(null);
                setInitialZoom(1);
            }
            if (vals.length === 0) {
                setTouchStart(null);
            }
        },
        [gestureEnabled],
    );

    const resetView = useCallback(() => {
        setZoom(1);
        setRotation(0);
        setPan({ x: 0, y: 0 });
    }, []);

    // Mouse wheel zoom handler
    const handleWheel = useCallback(
        (e) => {
            // if (e.ctrlKey || e.metaKey) {
            //     e.preventDefault();
            //     const delta = e.deltaY > 0 ? -0.1 : 0.1;
            //     const newZoom = Math.max(0.25, Math.min(3, zoom + delta));
            //     setZoom(newZoom);
            // }
        },
        [zoom],
    );

    // Container ref for targeted prevention and pointer capture
    const containerRef = useRef(null);
    // No global listeners; rely on pointer events + touch-action CSS like OpenSeadragon
    // Ensure Radix ScrollArea viewport also has touch-action: none so UA doesn't handle pinch-zoom
    useEffect(() => {
        const applyViewportTouchAction = () => {
            if (stackedContainerRef.current) {
                const vp = stackedContainerRef.current.querySelector('[data-radix-scroll-area-viewport]');
                if (vp && vp.style) {
                    vp.style.touchAction = 'none';
                    vp.style.msTouchAction = 'none';
                    vp.style.webkitUserSelect = 'none';
                }
            }

            // Apply touch-action to body to prevent viewport zoom
            if (typeof document !== 'undefined' && document.body) {
                document.body.style.touchAction = 'none';
                document.body.style.WebkitTouchCallout = 'none';
                document.body.style.WebkitUserSelect = 'none';
                document.body.style.userSelect = 'none';
            }
        };

        applyViewportTouchAction();

        // Cleanup function to restore body styles when component unmounts
        return () => {
            if (typeof document !== 'undefined' && document.body) {
                document.body.style.touchAction = '';
                document.body.style.WebkitTouchCallout = '';
                document.body.style.WebkitUserSelect = '';
                document.body.style.userSelect = '';
            }
        };
    }, [layout]);

    const formatFileSize = (bytes) => {
        if (!bytes) return 'Unknown';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        return new Date(dateString).toLocaleDateString();
    };

    if (!seasonDocument || pages.length === 0) {
        return (
            <Box className={classes.container} style={{ height: maxHeight }}>
                <Box className={classes.emptyState}>
                    <IconFileText size={48} />
                    <Text size='lg' mt='md'>
                        No document to display
                    </Text>
                    <Text size='sm' color='dimmed'>
                        Select a document to get started
                    </Text>
                </Box>
            </Box>
        );
    }
    const onCloseClick = () => {
        console.log('Close document viewer');
        onClose();
    };

    const scrollTop = stackedContainerRef.current?.querySelector('[data-radix-scroll-area-viewport]').scrollTop;

    return (
        <Box>
            <Group position='apart' sx={{ width: '100%', height: 60 }} px={'xs'} py={'xs'}>
                {/* <Crumbline /> */}
                <Group>
                    <UnstyledButton
                        onClick={() => {
                            onCloseClick();
                        }}>
                        <Group>
                            <IconArrowLeft />
                            <Title order={5} className='unhightable'>
                                Document Viewer
                            </Title>
                        </Group>
                    </UnstyledButton>
                </Group>

                <Title order={5} className='unhightable'>
                    {seasonDocument?.documentName || 'Document'}
                </Title>

                <Group>
                    {/* <ActionIcon onClick={() => setInfoShown(true)} variant='default'>
                        <IconInfoCircle size={18} />
                    </ActionIcon> */}
                    {/* <Tooltip label={showDebugLines ? 'Hide Debug Lines' : 'Show Debug Lines'}>
                        <ActionIcon 
                            onClick={() => setShowDebugLines(!showDebugLines)}
                            variant={showDebugLines ? 'filled' : 'outline'}
                            color={showDebugLines ? 'red' : 'gray'}
                        >
                            <IconDownload size={16} />
                        </ActionIcon>
                    </Tooltip> */}
                    <ActionIcon onClick={toggle} variant='default'>
                        <IconArrowsMaximize size={18} />
                    </ActionIcon>
                    <Button
                        data-autofocus
                        size='xs'
                        variant='outline'
                        onClick={() => {
                            onCloseClick();
                        }}>
                        Close
                    </Button>
                </Group>
            </Group>

            <Box className={classes.container} style={{ height: maxHeight - 60 }} ref={containerRef}>
                {/* Left Panel - Thumbnails */}
                {showThumbnailsState && (
                    <Box className={classes.thumbnailsPanel}>
                        <Box className={classes.thumbnailsHeader}>
                            <Group spacing='xs'>
                                <Text size='sm' weight={600}>
                                    Pages
                                </Text>
                                <Badge size='sm'>{pages.length}</Badge>
                            </Group>
                            {/* <Text size='sm' weight={600}>
                                Pages ({pages.length})
                            </Text> */}
                        </Box>
                        <ScrollArea className={classes.thumbnailsList}>
                            {pages.map((page, index) => (
                                <Box key={index} className={`${classes.thumbnailItem} ${index === currentPageIndex ? classes.thumbnailItemActive : ''}`} onClick={() => handlePageChange(index)}>
                                    {thumbnailLoadingStates.get(index) === 'loading' ? (
                                        <Skeleton
                                            height={120}
                                            className={classes.thumbnailImage}
                                            style={{
                                                width: '100%',
                                                borderRadius: 'inherit',
                                            }}
                                        />
                                    ) : (
                                        <img
                                            src={page.thumbnail || page.preview}
                                            alt={`Page ${index + 1}`}
                                            className={classes.thumbnailImage}
                                            onLoad={() => handleThumbnailLoad(index, true)}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                handleThumbnailLoad(index, false);
                                            }}
                                        />
                                    )}
                                    <Group className={classes.thumbnailInfo} position='apart'>
                                        <Text size='xs' weight={500}>
                                            Page {index + 1}
                                        </Text>
                                        {/* {page.hasContent && (
                                            <Badge size='xs' variant='dot' color='green'>
                                                Content
                                            </Badge>
                                        )} */}
                                        {page.productSuggestions && page.productSuggestions.length > 0 && (
                                            <Badge size='xs' variant='dot' color='blue'>
                                                {page.productSuggestions.length} {pluralize('product', page.productSuggestions.length)}
                                            </Badge>
                                        )}
                                    </Group>
                                </Box>
                            ))}
                        </ScrollArea>
                    </Box>
                )}

                {/* Center Panel - Preview */}

                <Box className={classes.previewPanel}>
                    <DocumentToolbar
                        document={seasonDocument}
                        currentPageIndex={currentPageIndex}
                        totalPages={pages.length}
                        onPageChange={handlePageChange}
                        zoom={zoom}
                        onZoomChange={handleZoomChange}
                        rotation={rotation}
                        onRotationChange={handleRotationChange}
                        layout={layout}
                        onLayoutChange={handleLayoutChange}
                        showThumbnails={showThumbnailsState}
                        onToggleThumbnails={() => setShowThumbnailsState(!showThumbnailsState)}
                        showMetadata={showMetadataState}
                        onToggleMetadata={() => setShowMetadataState(!showMetadataState)}
                        onFullscreen={toggle}
                        onDownload={handleDownload}
                        onPrint={handlePrint}
                        showPageControls={true}
                        showZoomControls={true}
                        showLayoutControls={true}
                        showPanelControls={true}
                    />

                    {layout === 'single' ? (
                        // Single Page Layout
                        <Box
                            className={classes.previewContent}
                            onPointerDown={handlePointerDown}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerUpOrCancel}
                            onPointerCancel={handlePointerUpOrCancel}
                            onWheel={handleWheel}>
                            {currentPage && currentPage.preview ? (
                                (() => {
                                    const loadingState = mainImageLoadingStates.get(currentPageIndex);
                                    console.log(`Rendering main image for page ${currentPageIndex}, loading state: ${loadingState}`);
                                    return loadingState === 'loading' ? (
                                        <Skeleton
                                            height={600}
                                            width={800}
                                            style={{
                                                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                                                transition: 'transform 0.2s ease',
                                                borderRadius: theme.radius.sm,
                                                boxShadow: theme.shadows.md,
                                                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[3],
                                                maxWidth: '100%',
                                                maxHeight: '100%',
                                            }}
                                        />
                                    ) : (
                                        <img
                                            ref={previewRef}
                                            src={currentPage.preview}
                                            alt={`${seasonDocument.documentName} - Page ${currentPageIndex + 1}`}
                                            className={classes.previewImage}
                                            style={{
                                                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                                                transition: 'transform 0.2s ease',
                                            }}
                                            onLoad={() => handleMainImageLoad(currentPageIndex, true)}
                                            onError={() => handleMainImageLoad(currentPageIndex, false)}
                                            onPointerDown={handlePointerDown}
                                            onPointerMove={handlePointerMove}
                                            onPointerUp={handlePointerUpOrCancel}
                                            onPointerCancel={handlePointerUpOrCancel}
                                        />
                                    );
                                })()
                            ) : (
                                <Box className={classes.emptyState}>
                                    <IconFileText size={48} />
                                    <Text size='lg' mt='md'>
                                        No preview available
                                    </Text>
                                    <Text size='sm' color='dimmed'>
                                        Page {currentPageIndex + 1}
                                    </Text>
                                </Box>
                            )}

                            {/* Preview Controls - Only in single layout */}
                            {/* <Box className={classes.previewControls}>
                                <Group spacing='xs'>
                                    <Tooltip label='Zoom Out'>
                                        <ActionIcon onClick={handleZoomOut} disabled={zoom <= 0.25}>
                                            <IconZoomOut size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label='Zoom In'>
                                        <ActionIcon onClick={handleZoomIn} disabled={zoom >= 3}>
                                            <IconZoomIn size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label='Rotate'>
                                        <ActionIcon onClick={handleRotate}>
                                            <IconRotate size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label='Reset View (Zoom, Rotation & Pan)'>
                                        <ActionIcon onClick={handleReset}>
                                            <IconDownload size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label={gestureEnabled ? 'Disable Touch Gestures' : 'Enable Touch Gestures'}>
                                        <ActionIcon 
                                            onClick={() => setGestureEnabled(!gestureEnabled)}
                                            variant={gestureEnabled ? 'filled' : 'outline'}
                                            color={gestureEnabled ? 'green' : 'gray'}
                                        >
                                            <IconGripVertical size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                </Group>
                            </Box> */}
                        </Box>
                    ) : (
                        // Stacked Pages Layout
                        <Box className={classes.previewContentStacked} style={{ position: 'relative' }}>
                            {/* Intersection Observer Trigger Lines - Fixed to viewport */}
                            {showDebugLines && (
                                <>
                                    <Box className={`${classes.intersectionObserverLine} ${classes.intersectionObserverLineTop}`} />
                                    <Box className={`${classes.intersectionObserverLine} ${classes.intersectionObserverLineBottom}`} />
                                </>
                            )}

                            <ScrollArea
                                ref={stackedContainerRef}
                                style={{
                                    height: 'calc(100% - 0px)',
                                    width: '100%', // Constrain to container width
                                }}
                                type='auto'
                                scrollHideDelay={500}
                                offsetScrollbars
                                // onPointerDown={handlePointerDown}
                                // onPointerMove={handlePointerMove}
                                // onPointerUp={handlePointerUpOrCancel}
                                // onPointerCancel={handlePointerUpOrCancel}
                                onWheel={handleWheel}>
                                <Box
                                    className={classes.stackedPagesContainer}
                                    style={{
                                        paddingTop: `${theme.spacing.xl}px`,
                                        paddingBottom: `${theme.spacing.xl}px`,
                                    }}>
                                    {/* Sticky Zoom Controls for Stacked Layout */}
                                    {/* <Box className={classes.stackedControls}>
                                    <Group spacing='xs'>
                                        <Tooltip label='Zoom Out'>
                                            <ActionIcon onClick={handleZoomOut} disabled={zoom <= 0.25}>
                                                <IconZoomOut size={16} />
                                            </ActionIcon>
                                        </Tooltip>
                                        <Tooltip label='Zoom In'>
                                            <ActionIcon onClick={handleZoomIn} disabled={zoom >= 3}>
                                                <IconZoomIn size={16} />
                                            </ActionIcon>
                                        </Tooltip>
                                        <Tooltip label='Reset Zoom'>
                                            <ActionIcon onClick={handleReset}>
                                                <IconDownload size={16} />
                                            </ActionIcon>
                                        </Tooltip>
                                    </Group>
                                </Box> */}
                                    {pages.map((page, index) => (
                                        <Box
                                            key={index}
                                            className={classes.stackedPageItem}
                                            style={{
                                                marginBottom: theme.spacing.lg, // Keep consistent margin
                                            }}>
                                            {/* Invisible marker for intersection observer */}
                                            <div ref={(el) => (pageRefs.current[index] = el)} className={classes.pageMarker} data-page-index={index} />

                                            {/* Page Number Badge */}
                                            <Badge
                                                variant={index === currentPageIndex ? 'filled' : 'filled'}
                                                color={index === currentPageIndex ? 'brand' : 'gray'}
                                                size='md'
                                                style={{
                                                    position: 'absolute',
                                                    top: -8,
                                                    left: 8,
                                                    zIndex: 10,
                                                }}>
                                                Page {index + 1}
                                            </Badge>

                                            {/* Page Content Indicators */}
                                            {/* <Group
                                            spacing='xs'
                                            style={{
                                                position: 'absolute',
                                                top: -8,
                                                right: 8,
                                                zIndex: 10,
                                            }}>
                                            {page.hasContent && (
                                                <Badge size='xs' variant='dot' color='green'>
                                                    Content
                                                </Badge>
                                            )}
                                            {page.productSuggestions && page.productSuggestions.length > 0 && (
                                                <Badge size='xs' variant='dot' color='blue'>
                                                    {page.productSuggestions.length} Products
                                                </Badge>
                                            )}
                                        </Group> */}

                                            {/* Page Image Container */}
                                            <Box
                                                style={{
                                                    width: `${800 * zoom}px`, // Fixed width that scales with zoom
                                                    minWidth: `${800 * zoom}px`, // Ensure it doesn't shrink
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'flex-start',
                                                }}>
                                                {page.preview ? (
                                                    mainImageLoadingStates.get(index) === 'loading' ? (
                                                        <Skeleton
                                                            height={600 * zoom}
                                                            width={800 * zoom}
                                                            style={{
                                                                borderRadius: theme.radius.sm,
                                                                boxShadow: theme.shadows.md,
                                                                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[3],
                                                                border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
                                                            }}
                                                        />
                                                    ) : (
                                                        <img
                                                            src={page.preview}
                                                            alt={`${seasonDocument.documentName} - Page ${index + 1}`}
                                                            className={classes.stackedPreviewImage}
                                                            style={{
                                                                width: '100%',
                                                                height: 'auto',
                                                                maxWidth: 'none', // Allow it to grow with container
                                                            }}
                                                            onLoad={() => handleMainImageLoad(index, true)}
                                                            onError={() => handleMainImageLoad(index, false)}
                                                            // onPointerDown={handlePointerDown}
                                                            // onPointerMove={handlePointerMove}
                                                            // onPointerUp={handlePointerUpOrCancel}
                                                            // onPointerCancel={handlePointerUpOrCancel}
                                                        />
                                                    )
                                                ) : (
                                                    <Box
                                                        className={classes.emptyState}
                                                        style={{
                                                            height: `${600 * zoom}px`,
                                                            width: '100%',
                                                            border: `2px dashed ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
                                                            borderRadius: theme.radius.sm,
                                                        }}>
                                                        <IconFileText size={48 * zoom} />
                                                        <Text size='lg' mt='md'>
                                                            No preview available
                                                        </Text>
                                                        <Text size='sm' color='dimmed'>
                                                            Page {index + 1}
                                                        </Text>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </ScrollArea>
                        </Box>
                    )}
                </Box>

                {/* Right Panel - Metadata */}
                {showMetadataState && (
                    <Box className={classes.metadataPanel}>
                        <Box className={classes.metadataHeader}>
                            <Text size='sm' weight={600}>
                                Document Info
                            </Text>
                        </Box>

                        <ScrollArea className={classes.metadataContent}>
                            {seasonDocument ? (
                                <Stack spacing='md'>
                                    {/* Document Info */}
                                    {/* <Box className={classes.metadataSection}>
                                        <Text className={classes.metadataLabel} size='sm'>
                                            Document
                                        </Text>
                                        <Text className={classes.metadataValue} size='sm'>
                                            {seasonDocument.documentName || 'Untitled'}
                                        </Text>
                                    </Box> */}

                                    {/* Current Page Info */}
                                    <Box className={classes.metadataSection}>
                                        <Title className='main-content-heading' order={5} mb='lg' mt='xs'>
                                            Current Page
                                        </Title>
                                        <Stack spacing='xs'>
                                            <Group position='apart'>
                                                <Text size='sm' color='dimmed'>
                                                    Page Number
                                                </Text>
                                                <Text size='sm'>
                                                    {currentPageIndex + 1} of {pages.length}
                                                </Text>
                                            </Group>
                                            <Group position='apart'>
                                                <Text size='sm' color='dimmed'>
                                                    Has Content
                                                </Text>
                                                <Badge size='sm' variant='light' color={currentPage?.hasContent ? 'green' : 'gray'}>
                                                    {currentPage?.hasContent ? 'Yes' : 'No'}
                                                </Badge>
                                            </Group>
                                            {currentPage?.productSuggestions && currentPage.productSuggestions.length > 0 && (
                                                <Group position='apart'>
                                                    <Text size='sm' color='dimmed'>
                                                        Product Suggestions
                                                    </Text>
                                                    <Badge size='sm' variant='light' color='blue'>
                                                        {currentPage.productSuggestions.length}
                                                    </Badge>
                                                </Group>
                                            )}
                                        </Stack>
                                    </Box>

                                    {/* Document Details */}
                                    <Box className={classes.metadataSection}>
                                        <Title className='main-content-heading' order={6} mb='lg'>
                                            Document Details
                                        </Title>
                                        <Stack spacing='xs'>
                                            <Group position='apart'>
                                                <Text size='sm' color='dimmed'>
                                                    Total Pages
                                                </Text>
                                                <Text size='sm'>{seasonDocument.getPageCount()}</Text>
                                            </Group>

                                            <Group position='apart'>
                                                <Text size='sm' color='dimmed'>
                                                    Pages with Content
                                                </Text>
                                                <Text size='sm'>{seasonDocument.getPagesWithText().length}</Text>
                                            </Group>
                                            <Group position='apart'>
                                                <Text size='sm' color='dimmed'>
                                                    Pages with Products
                                                </Text>
                                                <Text size='sm'>{seasonDocument.getPagesWithProductSuggestions().length}</Text>
                                            </Group>

                                            {/* {seasonDocument.bucket && (
                                                <Group position='apart'>
                                                    <Text size='sm' color='dimmed'>
                                                        Bucket
                                                    </Text>
                                                    <Text size='sm'>{seasonDocument.bucket}</Text>
                                                </Group>
                                            )}
                                            {seasonDocument.operator && (
                                                <Group position='apart'>
                                                    <Text size='sm' color='dimmed'>
                                                        Operator
                                                    </Text>
                                                    <Text size='sm'>{seasonDocument.operator}</Text>
                                                </Group>
                                            )}
                                            {seasonDocument.seasonPart && (
                                                <Group position='apart'>
                                                    <Text size='sm' color='dimmed'>
                                                        Season Part
                                                    </Text>
                                                    <Text size='sm'>{seasonDocument.seasonPart}</Text>
                                                </Group>
                                            )} */}
                                            {/* {seasonDocument.seasonSort && (
                                                <Group position='apart'>
                                                    <Text size='sm' color='dimmed'>
                                                        Season Sort
                                                    </Text>
                                                    <Text size='sm'>{seasonDocument.seasonSort}</Text>
                                                </Group>
                                            )} */}
                                        </Stack>
                                    </Box>

                                    {/* Page Text Content */}
                                    {currentPage?.text && (
                                        <Box className={classes.metadataSection}>
                                            <Title className='main-content-heading' order={6} mb='lg' mt='xs'>
                                                Page Copy
                                            </Title>
                                            <ScrollArea style={{ maxHeight: 150 }}>
                                                <Text className={classes.metadataValue} size='xs'>
                                                    {currentPage.text}
                                                </Text>
                                            </ScrollArea>
                                        </Box>
                                    )}

                                    {/* Document Summary */}
                                    {/* <Box className={classes.metadataSection}>
                                        <Title className='main-content-heading' order={6} mb='lg' mt='xs'>
                                            Document Summary
                                        </Title>
                                        <Stack spacing='xs'>
                                            <Group position='apart'>
                                                <Text size='sm' color='dimmed'>
                                                    Pages with Content
                                                </Text>
                                                <Text size='sm'>{seasonDocument.getPagesWithText().length}</Text>
                                            </Group>
                                            <Group position='apart'>
                                                <Text size='sm' color='dimmed'>
                                                    Pages with Products
                                                </Text>
                                                <Text size='sm'>{seasonDocument.getPagesWithProductSuggestions().length}</Text>
                                            </Group>
                                        </Stack>
                                    </Box> */}

                                    {/* Product Suggestions for Current Page */}
                                    {currentPage?.productSuggestions && currentPage.productSuggestions.length > 0 && (
                                        <Box className={classes.metadataSection}>
                                            <Title className='main-content-heading' order={6} mb='lg'>
                                                Product Suggestions
                                            </Title>
                                            <Group spacing='xs'>
{/* show a dot variant if that productId is in the currentpage styleSuggestions otherwise show default variant  */}
                                                {currentPage.productSuggestions?.map((productId, index) => {
                                                    const styleData = currentPage.styleSuggestions?.find(style => style.stylenumber === productId);
                                                    return (
                                                        <React.Fragment key={index}>
                                                            {styleData ? (
                                                                <SeasonStylePopover aggData={aggData} data={{...styleData, status: styleData.status || '', assets: styleData.assets || []}}>
                                                                    <Badge size='xs' variant='outline'  mb={6} >
                                                                        {productId}
                                                                    </Badge>
                                                                </SeasonStylePopover>
                                                            ) : (
                                                                <Badge size='xs' sx={{'::before': {display: 'none'}}} variant='dot'>
                                                                    {productId}
                                                                </Badge>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                                {/* {currentPage.productSuggestions.length > 5 && (
                                                    <Text size='xs' color='dimmed'>
                                                        +{currentPage.productSuggestions.length - 5} more
                                                    </Text>
                                                )} */}
                                            </Group>
                                        </Box>
                                    )}

                                    
                                </Stack>
                            ) : (
                                <Skeleton height={400} />
                            )}
                        </ScrollArea>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default DocumentViewer;
